import { inject, Injectable } from '@angular/core';
import { AmplitudeService } from '@brighthub/logging';
import {
  ActionEditorContext,
  ActionModalEntryPoint,
} from '../actions/action-editor-source';
import { actionDateInputValue } from '../actions/action-date';
import { CarePlanAction } from '../actions/actions.model';

export type ActionEditorEvent =
  | 'action_modal_opened'
  | 'action_create_started'
  | 'action_created'
  | 'action_updated'
  | 'action_status_changed'
  | 'action_reassigned'
  | 'action_validation_failed'
  | 'action_save_failed'
  | 'action_assignment_mismatch'
  | 'action_discard_prompted'
  | 'action_discard_canceled'
  | 'action_modal_closed';

/** Modal telemetry contains identifiers, codes and field names, never free text. */
@Injectable({ providedIn: 'root' })
export class ActionEditorAnalytics {
  private readonly _amplitude = inject(AmplitudeService);

  log(
    event: ActionEditorEvent,
    mode: 'add' | 'edit',
    context: ActionEditorContext & { entryPoint: ActionModalEntryPoint },
    action?: CarePlanAction,
    details: Record<string, unknown> = {},
  ): void {
    // Analytics failure must not turn a successful persistence into a failed save.
    try {
      this._amplitude.logEvent(event, {
        app: 'brightnav',
        mode,
        entry_point: context.entryPoint,
        ...(context.clientGuid ? { client_guid: context.clientGuid } : {}),
        ...((action?.clientId ?? context.clientId)
          ? { client_id: action?.clientId ?? context.clientId }
          : {}),
        case_id: action ? action.caseId : (context.caseId ?? null),
        ...(action
          ? { action_id: action.id, action_type: action.actionTypeCode }
          : {}),
        ...details,
      });
    } catch {
      // Keep the editor usable when telemetry is unavailable.
    }
  }

  saved(
    mode: 'add' | 'edit',
    context: ActionEditorContext & { entryPoint: ActionModalEntryPoint },
    updated: CarePlanAction,
    previous?: CarePlanAction,
  ): void {
    if (mode === 'add') {
      this.log('action_created', mode, context, updated, {
        priority: updated.priorityCode,
        has_due_date: !!updated.dueDate,
        client_promise: updated.clientPromise,
        owner_id: updated.assignedToId,
      });
      return;
    }
    if (!previous) return;
    const fields = {
      title: previous.title !== updated.title,
      description:
        (previous.description ?? '').trim() !==
        (updated.description ?? '').trim(),
      action_type: previous.actionTypeCode !== updated.actionTypeCode,
      status: previous.statusCode !== updated.statusCode,
      owner:
        updated.assignedToId != null &&
        previous.assignedToId !== updated.assignedToId,
      due_date:
        actionDateInputValue(previous.dueDate) !==
        actionDateInputValue(updated.dueDate),
      priority: previous.priorityCode !== updated.priorityCode,
      communication_preference:
        previous.communicationPreferenceCode !==
        updated.communicationPreferenceCode,
      client_promise: previous.clientPromise !== updated.clientPromise,
    };
    this.log('action_updated', mode, context, updated, {
      changed_fields: Object.entries(fields)
        .filter(([, changed]) => changed)
        .map(([field]) => field),
    });
    if (fields.status) {
      this.log('action_status_changed', mode, context, updated, {
        from_status: previous.statusCode,
        to_status: updated.statusCode,
      });
    }
    if (fields.owner) {
      this.log('action_reassigned', mode, context, updated, {
        from_owner_id: previous.assignedToId,
        to_owner_id: updated.assignedToId,
      });
    }
  }
}
